import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

type StaffRole = "support_agent" | "repair_manager" | "product_manager" | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  staffRole: StaffRole;
  isSuspended: boolean;
  isSuperAdmin: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
}

const SUPER_ADMIN_EMAILS = ["contact.abdelprod@gmail.com", "chawniphone1@gmail.com"];

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const [staffRole, setStaffRole] = useState<StaffRole>(null);

  const checkRoles = async (userId: string, email?: string) => {
    try {
      // 1. Check Roles
      const { data: roleData, error: roleError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);
      if (roleError) throw roleError;

      const roles = (roleData || []).map((r: any) => r.role);
      const adminStatus = roles.includes("admin");
      setIsAdmin(adminStatus);
      
      const emailToCheck = email || (await supabase.auth.getUser()).data.user?.email;
      const superAdminStatus = emailToCheck ? SUPER_ADMIN_EMAILS.includes(emailToCheck) : false;
      setIsSuperAdmin(superAdminStatus);

      if (roles.includes("support_agent")) setStaffRole("support_agent");
      else if (roles.includes("repair_manager")) setStaffRole("repair_manager");
      else if (roles.includes("product_manager")) setStaffRole("product_manager");
      else setStaffRole(null);

      // 2. Check Suspension (Exclude super admins)
      if (!superAdminStatus) {
        const { data: profile, error: profileError } = await (supabase as any)
          .from("profiles")
          .select("is_suspended")
          .eq("id", userId)
          .maybeSingle();
        
        if (!profileError && profile) {
          setIsSuspended(!!profile.is_suspended);
        } else {
          setIsSuspended(false);
        }
      } else {
        setIsSuspended(false);
      }
    } catch (error) {
      console.warn("AuthContext: Failed to fetch user roles or suspension state:", error);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsSuspended(false);
      setStaffRole(null);
    }
  };

  useEffect(() => {
    // Initial fetch of session
    const initAuth = async () => {
      setLoading(true);
      try {
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error("Timeout")), 5000)
        );
        const sessionPromise = supabase.auth.getSession();
        const result: any = await Promise.race([sessionPromise, timeoutPromise]);
        const session = result?.data?.session;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          await checkRoles(session.user.id, session.user.email);
        }
      } catch (error: any) {
        console.warn("AuthContext: Initialization slow/timeout or error. Loading as guest.", error?.message);
        setSession(null);
        setUser(null);
        setIsAdmin(false);
        setStaffRole(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log("AuthContext: Auth state change event:", _event);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await checkRoles(session.user.id, session.user.email);
      } else {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsSuspended(false);
        setStaffRole(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    const trimmedEmail = email.trim();
    let timeoutId: any;
    try {
      console.log("AuthContext: Calling signUp for:", trimmedEmail);
      const timeoutPromise = new Promise((_, reject) => 
        timeoutId = setTimeout(() => reject(new Error("Network timeout: The server took too long to respond.")), 15000)
      );
      
      const signUpReq = supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { first_name: firstName, last_name: lastName },
        },
      });
      
      const { data, error }: any = await Promise.race([signUpReq, timeoutPromise]);
      clearTimeout(timeoutId);
      return { error };
    } catch (error: any) {
      clearTimeout(timeoutId);
      console.error("AuthContext: signUp error:", error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    const trimmedEmail = email.trim();
    let timeoutId: any;
    try {
      console.log("AuthContext: Calling signIn for:", trimmedEmail);
      const timeoutPromise = new Promise((_, reject) => 
        timeoutId = setTimeout(() => reject(new Error("Network timeout: The server took too long to respond.")), 15000)
      );
      
      const signInReq = supabase.auth.signInWithPassword({ 
        email: trimmedEmail, 
        password 
      });
      
      const { data, error }: any = await Promise.race([signInReq, timeoutPromise]);
      clearTimeout(timeoutId);

      if (error) {
        throw error;
      }
      return { data, error: null };
    } catch (err: any) {
      clearTimeout(timeoutId);
      console.error("AuthContext: signIn error", err);
      // toast({ title: "Error", description: err.message, variant: "destructive" }); // Commented out as `toast` is not defined in the original document
      return { error: err };
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
          },
        },
      });
      if (error) throw error;
      return { data, error: null };
    } catch (err: any) {
      console.error("AuthContext: Google signIn error", err);
      return { error: err };
    }
  };

  const signOut = async () => {
    try {
      console.log("AuthContext: Signing out (with 3s timeout)...");
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Timeout")), 3000)
      );
      await Promise.race([supabase.auth.signOut(), timeoutPromise]);
    } catch (error) {
      console.warn("AuthContext: signOut timed out or failed, but clearing local state anyway.", error);
      // Force clear local state if signout hangs
      setSession(null);
      setUser(null);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsSuspended(false);
      setStaffRole(null);
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, isSuperAdmin, isSuspended, staffRole, signUp, signIn, signInWithGoogle, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
